import { useState, useSyncExternalStore } from 'react';
import { AlertCircle } from 'lucide-react';
import { getAmiPersistStatus, subscribeAmiPersistStatus } from '../lib/amiPersistStatus';
import { purgeLocalProject } from '../lib/localProjects';

/**
 * Publishes the outcome of the most recent AMI interval operation to the DOM,
 * and surfaces a persistent banner when one fails.
 *
 * Renders nothing until the first completed operation. Only 'ok' and 'failed'
 * reach the store — skips are never published — so data-state carries only
 * those two values.
 *
 * A failure banner varies along two axes, and both differences are the point:
 *
 *   RECOVERABLE   — a transient failure. Offers Retry, no dismiss. The user
 *                   should not be able to wave the warning away and forget it.
 *   UNRECOVERABLE — the project is gone from the server. Retry can never
 *                   succeed, so offering it would trap the user behind a red
 *                   bar forever. The exit is to purge the orphaned local copy.
 *
 *   SAVE          — the data reached this session but not the server.
 *   DELETE        — the reverse, and the more alarming of the two: the data the
 *                   user just asked us to destroy is still on the server, and
 *                   will reappear on the next load. Saying "not saved" there
 *                   would describe the opposite of what went wrong.
 */
export default function AmiPersistStatus() {
  const result = useSyncExternalStore(subscribeAmiPersistStatus, getAmiPersistStatus);
  const [retrying, setRetrying] = useState(false);
  const [clearing, setClearing] = useState(false);

  if (!result) return null;

  // Absent op means 'save' — the field postdates every save call site.
  const isDelete = result.op === 'delete';

  const handleRetry = async () => {
    if (!result.retry) return;
    setRetrying(true);
    try {
      // A successful retry publishes an 'ok' result, which re-renders this
      // component with no banner. A failed retry publishes a newer 'failed',
      // and the banner simply stays. No manual dismiss logic either way.
      await result.retry();
    } finally {
      setRetrying(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      // purgeLocalProject is the established DELETE primitive (localProjects.ts,
      // added in 39053cd). It removes the trueload_projects_<uid> entry, the
      // IndexedDB AMI record, and any current/last-project pointer aimed at this
      // id. Do NOT reimplement any of that here.
      await purgeLocalProject(result.projectId);
      // The completion snapshot is the one piece of this project's footprint
      // purgeLocalProject does not own — it lives in the validation module's
      // own key space.
      try {
        localStorage.removeItem(`trueload_validation_${result.projectId}`);
      } catch {
        // localStorage may be unavailable in some environments
      }
    } catch (error) {
      console.error('[AmiPersistStatus] failed to purge orphaned local project:', error);
    } finally {
      // Reload regardless. The in-memory app state still references a project
      // that no longer exists anywhere, and a partial purge is still better
      // reloaded than left half-applied on screen.
      window.location.reload();
    }
  };

  const detail = (
    <p className="mt-2 text-xs text-red-200 break-words">
      {result.caller} · {result.projectId}
      {result.error && ` · ${result.error}`}
    </p>
  );

  return (
    <>
      <div
        data-testid="ami-persist-status"
        data-state={result.state}
        data-op={result.op ?? 'save'}
        data-inserted={String(result.inserted)}
        data-sent={String(result.sent)}
        data-project={result.projectId}
        data-caller={result.caller}
        data-seq={String(result.seq)}
        data-recoverable={String(result.recoverable)}
        style={{ display: 'none' }}
      />

      {result.state === 'failed' && (
        <div
          data-testid="ami-persist-banner"
          role="alert"
          className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white shadow-lg"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-start gap-3">
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              {result.recoverable ? (
                <>
                  <p className="text-sm font-semibold">
                    {isDelete
                      ? 'Your interval data was not deleted'
                      : 'Your interval data was not saved'}
                  </p>
                  <p className="text-sm text-red-100 mt-1">
                    {isDelete
                      ? 'It has been cleared from this session, but it is still stored on your ' +
                        'project and will come back the next time you open it. Do not treat this ' +
                        'data as deleted until this is resolved.'
                      : 'The data is still loaded in this session and your calculation results are ' +
                        'correct, but it has not been stored to your project. Do not rely on this ' +
                        'project being saved until this is resolved.'}
                  </p>
                  <button
                    onClick={handleRetry}
                    disabled={retrying}
                    data-testid="btn-ami-persist-retry"
                    className="mt-3 px-4 py-2 bg-white text-red-700 rounded-md text-sm font-semibold hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {retrying
                      ? 'Retrying...'
                      : isDelete ? 'Retry delete' : 'Retry save'}
                  </button>
                  {detail}
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold">This project no longer exists</p>
                  <p className="text-sm text-red-100 mt-1">
                    {isDelete
                      ? 'Your interval data could not be deleted because this project is no longer ' +
                        'on the server — which means the data is gone with it. The project may have ' +
                        'been deleted from another device or another browser. The copy stored in ' +
                        'this browser cannot be recovered.'
                      : 'Your interval data could not be saved because this project is no longer on ' +
                        'the server. It may have been deleted from another device or another browser. ' +
                        'The copy stored in this browser cannot be recovered.'}
                  </p>
                  <button
                    onClick={handleClear}
                    disabled={clearing}
                    data-testid="btn-ami-persist-clear"
                    className="mt-3 px-4 py-2 bg-white text-red-700 rounded-md text-sm font-semibold hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {clearing ? 'Removing...' : 'Remove local copy'}
                  </button>
                  {detail}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
